import { motion } from 'framer-motion';
import { Landmark, Building, Briefcase, Handshake } from 'lucide-react';

const segments = [
  {
    icon: Landmark,
    title: 'Federal Agencies',
    description: 'Supporting mission-critical programs across defense, civilian, and intelligence sectors with cleared, credentialed teams.',
  },
  {
    icon: Building,
    title: 'State & Local Governments',
    description: 'Modernizing public services with scalable technology solutions and streamlined infrastructure support.',
  },
  {
    icon: Briefcase,
    title: 'Prime Contractors',
    description: 'Augmenting prime contractor capabilities with specialized execution teams and flexible teaming structures.',
  },
  {
    icon: Handshake,
    title: 'Public-Private Partnerships',
    description: 'Bridging government and private sector innovation through strategic collaboration and joint program delivery.',
  },
];

const fadeUp = {
  hidden: { opacity: 0, y: 20 },
  visible: (i) => ({
    opacity: 1,
    y: 0,
    transition: { duration: 0.5, delay: i * 0.12, ease: [0.25, 0.4, 0.25, 1] }
  })
};

export default function WhoWeServe() {
  return (
    <section
      id="who-we-serve"
      data-testid="who-we-serve-section"
      className="py-24 lg:py-32 bg-[#0A0A0A]"
    >
      <div className="max-w-7xl mx-auto px-6">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.6 }}
          className="mb-16"
        >
          <p className="font-body text-[#B8102E] text-xs tracking-[0.3em] uppercase mb-4">
            Our Clients
          </p>
          <h2 className="font-heading text-3xl sm:text-4xl font-bold text-white tracking-tight mb-4">
            Who We Serve
          </h2>
          <p className="font-body text-[#A3A3A3] max-w-xl">
            We support set-aside contracts and maintain flexible teaming structures as both prime and subcontractor.
          </p>
        </motion.div>

        <div className="grid md:grid-cols-2 gap-6">
          {segments.map((seg, i) => {
            const Icon = seg.icon;
            return (
              <motion.div
                key={seg.title}
                custom={i}
                initial="hidden"
                whileInView="visible"
                viewport={{ once: true, margin: "-50px" }}
                variants={fadeUp}
                data-testid={`serve-card-${i}`}
                className="group p-8 bg-[#141414] border border-[#2A2A2A] rounded-lg hover:border-[#B8102E]/50 transition-all duration-300"
              >
                <div className="flex items-start gap-4">
                  <div className="flex-shrink-0 w-12 h-12 rounded-lg bg-[#B8102E]/10 flex items-center justify-center group-hover:bg-[#B8102E]/20 transition-colors duration-300">
                    <Icon className="h-6 w-6 text-[#B8102E]" />
                  </div>
                  <div>
                    <h3 className="font-heading text-lg font-semibold text-white mb-2">
                      {seg.title}
                    </h3>
                    <p className="font-body text-sm text-[#A3A3A3] leading-relaxed">
                      {seg.description}
                    </p>
                  </div>
                </div>
              </motion.div>
            );
          })}
        </div>
      </div>
    </section>
  );
}
